type MaybePromise<T> = T | Promise<T>

declare const presentationResponseType: unique symbol

const DefaultPresentationResponseNamespace = "Florisoft.Application.Responses"

export interface PresentationResponseDefinition<TResponse, TResult = void> {
  readonly type: string
  readonly namespace: string
  readonly [presentationResponseType]?: (response: TResponse) => TResult
}

export type PresentationResponseHandler<TResponse, TResult = void> = (
  response: Readonly<TResponse>,
  signal: AbortSignal | undefined,
  invocation: PresentationInvocation
) => MaybePromise<TResult>

export interface PresentationInvocation {
  readonly mode: "present" | "notify"
  readonly hasResultHandler?: boolean
}

export interface PresentationNotificationOptions<TResult> {
  readonly signal?: AbortSignal
  readonly onResult?: (result: TResult) => MaybePromise<void>
}

export class PresentationCancelledError extends Error {
  constructor(message = "The presentation was cancelled.") {
    super(message)
    this.name = "PresentationCancelledError"
  }
}

export interface Presenter {
  notify<TResponse, TResult = void>(
    definition: PresentationResponseDefinition<TResponse, TResult>,
    response: TResponse,
    options?: PresentationNotificationOptions<TResult>
  ): void

  present<TResponse, TResult = void>(
    definition: PresentationResponseDefinition<TResponse, TResult>,
    response: TResponse,
    signal?: AbortSignal
  ): Promise<TResult>

  register<TResponse, TResult = void>(
    definition: PresentationResponseDefinition<TResponse, TResult>,
    handler: PresentationResponseHandler<TResponse, TResult>
  ): () => void
}

export class PresenterUnhandledResponseError extends Error {
  constructor(
    public readonly responseType: string,
    public readonly responseNamespace: string
  ) {
    super(`No presenter handled ${responseNamespace}.${responseType}.`)
    this.name = "PresenterUnhandledResponseError"
  }
}

export function definePresentationResponse<TResponse, TResult = void>(options: {
  readonly type: string
  readonly namespace?: string
}): PresentationResponseDefinition<TResponse, TResult> {
  const type = normalizeIdentityPart(options.type, "Presentation response type")
  const namespace = normalizeNamespace(options.namespace)
  return Object.freeze({ type, namespace })
}

/** @internal */
export type PresenterResolution<T> = { readonly handled: true; readonly value: T } | { readonly handled: false }

/** @internal */
export interface PresenterResolver {
  resolve<TResult>(
    scopeIds: readonly string[],
    type: string,
    namespace: string | undefined,
    value: unknown,
    signal?: AbortSignal,
    invocation?: PresentationInvocation
  ): MaybePromise<PresenterResolution<TResult>>
  reportNotificationError(error: unknown, type: string, namespace?: string, signal?: AbortSignal): void
}

/** @internal */
export interface PresenterRuntime {
  readonly root: Presenter
  readonly resolver: PresenterResolver
  createScoped(scopeIds: readonly string[]): Presenter
}

type UntypedPresentationHandler = (
  value: unknown,
  signal: AbortSignal | undefined,
  invocation: PresentationInvocation
) => MaybePromise<unknown>

interface PresenterRegistration {
  readonly handler: UntypedPresentationHandler
}

/** @internal */
export function createPresenterRuntime(
  reportError: (error: unknown, type: string, namespace: string) => void = (error) => console.error(error)
): PresenterRuntime {
  const registrations = new Map<string | null, Map<string, PresenterRegistration[]>>()
  const reportNotificationError = (error: unknown, type: string, namespace?: string, signal?: AbortSignal) => {
    if (signal?.aborted || error instanceof PresentationCancelledError) return
    try {
      reportError(error, type, normalizeNamespace(namespace))
    } catch (reportingError) {
      console.error("Could not report a Presenter notification failure.", reportingError)
    }
  }

  const register = (
    scopeId: string | null,
    definition: PresentationResponseDefinition<unknown, unknown>,
    handler: UntypedPresentationHandler
  ) => {
    let scopeRegistrations = registrations.get(scopeId)
    if (scopeRegistrations == null) {
      scopeRegistrations = new Map()
      registrations.set(scopeId, scopeRegistrations)
    }

    const key = getDefinitionKey(definition)
    let scopeEntries = scopeRegistrations.get(key)
    if (scopeEntries == null) {
      scopeEntries = []
      scopeRegistrations.set(key, scopeEntries)
    }
    const registration: PresenterRegistration = { handler }
    scopeEntries.push(registration)
    let registered = true

    return () => {
      if (!registered) return
      registered = false

      const index = scopeEntries.indexOf(registration)
      if (index >= 0) scopeEntries.splice(index, 1)
      if (scopeEntries.length === 0) scopeRegistrations!.delete(key)
      if (scopeRegistrations!.size === 0) registrations.delete(scopeId)
    }
  }

  const resolve = <TResult>(
    scopeIds: readonly string[],
    type: string,
    namespace: string | undefined,
    value: unknown,
    signal?: AbortSignal,
    invocation: PresentationInvocation = { mode: "present" }
  ): MaybePromise<PresenterResolution<TResult>> => {
    signal?.throwIfAborted()
    const key = getIdentityKey(type, namespace)
    for (const scopeId of [...scopeIds, null]) {
      const scopeEntries = registrations.get(scopeId)?.get(key)
      const registration = scopeEntries?.at(-1)
      if (registration == null) continue

      let result = registration.handler(value, signal, invocation)
      if (signal != null) {
        const pending = result
        let abort!: () => void
        result = new Promise((resolve, reject) => {
          abort = () => reject(signal.reason)
          signal.addEventListener("abort", abort, { once: true })
          Promise.resolve(pending).then(resolve, reject)
          if (signal.aborted) abort()
        }).finally(() => signal.removeEventListener("abort", abort))
      }
      if (result instanceof Promise) {
        return result.then((resolved) => ({
          handled: true,
          value: resolved as TResult,
        }))
      }
      return { handled: true, value: result as TResult }
    }

    return { handled: false }
  }

  const createPresenter = (scopeIds: readonly string[], registrationScopeId: string | null): Presenter =>
    Object.freeze({
      notify<TResponse, TResult = void>(
        definition: PresentationResponseDefinition<TResponse, TResult>,
        response: TResponse,
        options?: PresentationNotificationOptions<TResult>
      ) {
        const signal = options?.signal
        const failed = (error: unknown) => reportNotificationError(error, definition.type, definition.namespace, signal)
        try {
          const resolution = resolve(scopeIds, definition.type, definition.namespace, response, signal, {
            mode: "notify",
            hasResultHandler: options?.onResult != null,
          })
          void Promise.resolve(resolution)
            .then((resolved) => {
              if (!resolved.handled) throw new PresenterUnhandledResponseError(definition.type, definition.namespace)
              signal?.throwIfAborted()
              return options?.onResult?.(resolved.value as TResult)
            })
            .catch(failed)
        } catch (error) {
          failed(error)
        }
      },

      async present<TResponse, TResult = void>(
        definition: PresentationResponseDefinition<TResponse, TResult>,
        response: TResponse,
        signal?: AbortSignal
      ) {
        const resolution = resolve(scopeIds, definition.type, definition.namespace, response, signal)
        const resolved = resolution instanceof Promise ? await resolution : resolution
        if (!resolved.handled) {
          throw new PresenterUnhandledResponseError(definition.type, definition.namespace)
        }
        return resolved.value as TResult
      },

      register<TResponse, TResult = void>(
        definition: PresentationResponseDefinition<TResponse, TResult>,
        handler: PresentationResponseHandler<TResponse, TResult>
      ) {
        return register(
          registrationScopeId,
          definition as PresentationResponseDefinition<unknown, unknown>,
          handler as UntypedPresentationHandler
        )
      },
    })

  return Object.freeze({
    root: createPresenter([], null),
    resolver: Object.freeze({ resolve, reportNotificationError }),
    createScoped(scopeIds: readonly string[]) {
      const ids = Object.freeze([...scopeIds])
      return createPresenter(ids, ids[0] ?? null)
    },
  })
}

function getDefinitionKey(definition: PresentationResponseDefinition<unknown, unknown>) {
  return getIdentityKey(definition.type, definition.namespace)
}

function getIdentityKey(type: string, namespace?: string) {
  return `${normalizeNamespace(namespace)}\u0000${normalizeIdentityPart(type, "Presentation response type")}`
}

function normalizeNamespace(namespace?: string) {
  return namespace == null
    ? DefaultPresentationResponseNamespace
    : normalizeIdentityPart(namespace, "Presentation response namespace")
}

function normalizeIdentityPart(value: string, label: string) {
  const normalized = value.trim()
  if (normalized.length === 0) throw new Error(`${label} must not be empty.`)
  return normalized
}
